import { isInterface } from "../utils/TypeUtil";
import { Map } from "./Models";

export interface SelectOption {
    value: string;
    label: string;
}

export interface SelectOptionGroup
extends SelectOption {
    options: SelectOptionGroup[];
}

export const findSelectOption = (
    options: SelectOption[],
    value: string | number
): SelectOption | undefined => {
    return options?.find((option) => option.value === value);
};

export const findSelectOptionGroupByLabel = (
    optionGroups: SelectOptionGroup[],
    value: string | number
): SelectOptionGroup | undefined => {
    return optionGroups?.find((optionGroup) => optionGroup.label === value);
};

export const findSelectOptionGroupByLabelSubOptionValues = (
    optionGroups: SelectOptionGroup[],
    value: string | number
): string[] => {
    return findSelectOptionGroupByLabel(optionGroups, value)?.options?.map((option) => option.value) ?? [] as string[];
};

export const toSelectOptionValues = (
    options: SelectOption[]
): string[] => {
    return options.map(option => option.value);
};

export const isAllSelectOptionsSelected = (
    options: SelectOption[],
    selectedValues: string[]
) => {
    return options.every(option => selectedValues.includes(option.value));
};

export const filterSelectOptionsSelected = (
    options: SelectOption[],
    selectedValues: string[]
) => {
    return options.filter(option => selectedValues.includes(option.value));
};

export const filterSelectOptionGroupsSelectedBySubOptions = (
    optionGroups: SelectOptionGroup[],
    selectedValues: string[]
) => {
    return optionGroups.filter((optionGroup) => optionGroup.options.some((option) => selectedValues.includes(option.value)));
};

export const selectOptionGroupLabelBySelectOptionGroupSubOptions = (
    optionGroups: SelectOptionGroup[],
)=> {
    if (!optionGroups?.length) return {} as Map<string>;

    const tempMap = {} as Map<string>;
    optionGroups.forEach((optionGroup) => {
        optionGroup.options.forEach((option) => {
            tempMap[option.value] = optionGroup.label;
        });
    });

    return tempMap;
};

export type Status = "INFO" | "SUCCESS" | "WARNING" | "ERROR" | "NONE";

export interface ResultMessage {
    status: Status;
    title?: string;
    message?: string;
}

export const buildResultMessage =
    (status: Status, message?: string, title?: string): ResultMessage => {
        return {
            status,
            title,
            message,
        };
    };

export const  buildInfoMessage =
    (message?: string, title?: string): ResultMessage => buildResultMessage("INFO", message, title);

export const  buildErrorMessage =
    (message?: string, title?: string): ResultMessage => buildResultMessage("ERROR", message, title);

export const isResultMessage = (obj: unknown): obj is ResultMessage =>
    isInterface<ResultMessage>(
        obj,
        [ "status", "title", "message" ],
        [ "status" ],
    );

export type DataAction = "CREATE" | "UDPATE" | "DELETE";

export interface DataEventMessage {
    action: DataAction;
    entityType: string;
    entityId: string;
    cacheKeys?: unknown[][],
}

export const isDataEventMessage = (obj: unknown): obj is DataEventMessage =>
    isInterface<DataEventMessage>(
        obj,
        [ "action", "entityType", "entityId", "cacheKeys" ],
        [ "action", "entityType", "entityId" ],
    );

export interface EntityCollection<T> {
    pageIndex: number;
    requestedPageSize: number;
    total: number;
    items: T[];
}
